import React, { useState, useEffect, useCallback } from 'react';
import {
  Slideout,
  Typography,
  Button,
  Icon,
  Tag,
  Switch,
  Scrollbar,
  CircularProgress,
} from '@ann/web-components';
import {
  SlideoutContainer,
  MainContentWrapper,
  ScrollableContentWrapper,
  ScrollableContent,
  NavigationBar,
  Breadcrumb,
  SummarySection,
  MetadataSection,
  SectionHeader,
  SectionBody,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
  TopicSection,
  TopicSectionHeader,
  TopicItem,
  SwitchWrapper,
  TopicContent,
  SlideoutActions,
} from './SlideoutDocumentDetail.styled';

const SlideoutDocumentDetail = ({ open, onClose, documentData }) => {
  const [loading, setLoading] = useState(false);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (!open || !documentData?.uuid) {
      return;
    }

    const fetchDocumentDetails = async () => {
      setLoading(true);

      // TODO: Reemplazar con llamada real a API cuando esté disponible
      // Por ahora usamos datos mock basados en documentData
      const mockDetails = {
        uuid: documentData.uuid,
        title: documentData.name || documentData.title || 'Document',
        driveName: documentData.driveName || 'Unknown Drive',
        summary:
          documentData.description ||
          documentData.summary ||
          'This document contains information that your agent can use to answer questions.',
        lastUpdated: documentData.updated_at || documentData.created_at || new Date().toLocaleDateString(),
        audience: documentData.audience || 'Internal',
        status: documentData.state || documentData.status || 'Active',
        owner: documentData.owner || documentData.createdBy || 'Unknown',
        version: documentData.version || documentData.metadata?.version || '1.0.0',
        topics: documentData.topics || [
          {
            id: 'default',
            name: 'General Information',
            description: 'General information from this document.',
            enabled: true,
          },
        ],
      };

      setDocumentDetails(mockDetails);
      setTopics(mockDetails.topics);
      setSelectAll(mockDetails.topics.every((topic) => topic.enabled));
      setLoading(false);
    };

    fetchDocumentDetails();
  }, [open, documentData]);

  // Reset cuando se cierra
  useEffect(() => {
    if (!open) {
      setDocumentDetails(null);
      setTopics([]);
      setSelectAll(false);
    }
  }, [open]);

  const handleGoToDocument = useCallback(() => {
    // TODO: Implementar navegación al documento
    console.log('Navigate to document:', documentDetails?.uuid);
  }, [documentDetails]);

  const handleSelectAllToggle = useCallback((value) => {
    setSelectAll(value);
    setTopics((prev) => prev.map((topic) => ({ ...topic, enabled: value })));
  }, []);

  const handleTopicToggle = useCallback((topicId, value) => {
    setTopics((prev) => {
      const updated = prev.map((topic) =>
        topic.id === topicId ? { ...topic, enabled: value } : topic
      );
      setSelectAll(updated.every((topic) => topic.enabled));
      return updated;
    });
  }, []);

  if (!documentData && !loading) {
    return null;
  }

  return (
    <Slideout onClose={onClose} size="medium" open={open}>
      <Slideout.HeaderActions>
        <Icon name="arrowLeft" size="large" onClick={onClose} />
      </Slideout.HeaderActions>

      <SlideoutContainer>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress size="medium" />
          </div>
        ) : (
          documentDetails && (
            <>
              <Typography category="title" size="large" weight="bold">
                {documentDetails.title}
              </Typography>

              <MainContentWrapper>
                <NavigationBar>
                  <Button
                    iconLeft="arrowLeft"
                    label="Back"
                    size="small"
                    type="tertiary"
                    onClick={onClose}
                  />
                  <Breadcrumb>
                    <Typography category="body" size="small" weight="regular" color="subtitle">
                      Drive / {documentDetails.driveName} / {documentDetails.title}
                    </Typography>
                  </Breadcrumb>
                </NavigationBar>

                <ScrollableContentWrapper>
                  <Scrollbar size="small">
                    <ScrollableContent>
                      <SummarySection>
                        <SectionHeader>
                          <Icon color="primary-alt" name="aiChat" />
                          <Typography category="subtitle" size="medium" weight="bold">
                            Summary
                          </Typography>
                        </SectionHeader>
                        <SectionBody>
                          <Typography category="body" size="medium" weight="regular" color="body">
                            {documentDetails.summary}
                          </Typography>
                        </SectionBody>
                      </SummarySection>

                      <MetadataSection>
                        <MetadataItem>
                          <MetadataLabel>
                            <Typography category="subtitle" size="medium" weight="bold" color="subtitle">
                              Last Updated
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Typography category="body" size="small" weight="regular" color="body">
                              {documentDetails.lastUpdated}
                            </Typography>
                          </MetadataValue>
                        </MetadataItem>

                        <MetadataItem>
                          <MetadataLabel>
                            <Typography category="subtitle" size="medium" weight="bold" color="subtitle">
                              Audience
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Typography category="body" size="small" weight="regular" color="body">
                              {documentDetails.audience}
                            </Typography>
                          </MetadataValue>
                        </MetadataItem>

                        <MetadataItem>
                          <MetadataLabel>
                            <Typography category="subtitle" size="medium" weight="bold" color="subtitle">
                              Status
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Tag
                              color="informative"
                              size="medium"
                              text={documentDetails.status}
                            />
                          </MetadataValue>
                        </MetadataItem>

                        <MetadataItem>
                          <MetadataLabel>
                            <Typography category="subtitle" size="medium" weight="bold" color="subtitle">
                              Owner
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Typography category="body" size="small" weight="regular" color="body">
                              {documentDetails.owner}
                            </Typography>
                          </MetadataValue>
                        </MetadataItem>

                        <MetadataItem>
                          <MetadataLabel>
                            <Typography category="subtitle" size="medium" weight="bold" color="subtitle">
                              Version
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Typography category="body" size="small" weight="regular" color="body">
                              {documentDetails.version}
                            </Typography>
                          </MetadataValue>
                        </MetadataItem>
                      </MetadataSection>

                      {topics.length > 0 && (
                        <TopicSection>
                          <TopicSectionHeader>
                            <Typography category="subtitle" size="medium" weight="bold" color="subtitle">
                              Topics
                            </Typography>
                            <Typography category="body" size="medium" weight="regular" color="body">
                              Select the topics you want your agent to access
                            </Typography>
                          </TopicSectionHeader>

                          <TopicItem>
                            <SwitchWrapper>
                              <Switch
                                checked={selectAll}
                                onChange={handleSelectAllToggle}
                                size="medium"
                              />
                            </SwitchWrapper>
                            <TopicContent>
                              <Typography category="body" size="medium" weight="bold">
                                Select All Topics
                              </Typography>
                            </TopicContent>
                          </TopicItem>

                          {topics.map((topic) => (
                            <TopicItem key={topic.id}>
                              <SwitchWrapper>
                                <Switch
                                  checked={topic.enabled}
                                  onChange={(value) => handleTopicToggle(topic.id, value)}
                                  size="medium"
                                />
                              </SwitchWrapper>
                              <TopicContent>
                                <Typography category="subtitle" size="small" weight="bold" color="subtitle">
                                  {topic.name}
                                </Typography>
                                <Typography category="body" size="medium" weight="regular" color="body">
                                  {topic.description}
                                </Typography>
                              </TopicContent>
                            </TopicItem>
                          ))}
                        </TopicSection>
                      )}
                    </ScrollableContent>
                  </Scrollbar>
                </ScrollableContentWrapper>

                <SlideoutActions>
                  <Button
                    label="Go to document"
                    size="medium"
                    type="primary"
                    onClick={handleGoToDocument}
                  />
                </SlideoutActions>
              </MainContentWrapper>
            </>
          )
        )}
      </SlideoutContainer>
    </Slideout>
  );
};

export default SlideoutDocumentDetail;

